import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const stats = [
  { value: '100+', label: "People We've Helped", icon: FiIcons.FiUsers },
  { value: 'Honest', label: 'Straightforward Pricing', icon: FiIcons.FiDollarSign },
  { value: 'Personal', label: 'One-on-One Attention', icon: FiIcons.FiMessageSquare },
  { value: '5★', label: 'Google Rating', icon: FiIcons.FiStar },
];

const values = [
  { icon: FiIcons.FiHeart, title: 'We Actually Care', desc: "You're not a ticket in a queue. When you work with us, you're working directly with us — two people who genuinely want to see you succeed." },
  { icon: FiIcons.FiMessageSquare, title: 'We Keep It Real', desc: "No fluff, no overselling. We'll be honest with you about what we can do and make sure every word we write truly reflects who you are." },
  { icon: FiIcons.FiShield, title: 'Your Comfort Comes First', desc: "We move at your pace. Have questions? Just ask. We're approachable, patient, and always happy to talk things through." },
];

const About = () => (
  <section id="about" className="py-24 bg-gray-50">
    <div className="container mx-auto px-6 max-w-7xl">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-sm font-bold text-brand-accent uppercase tracking-widest mb-2">About Us</h2>
          <h3 className="text-3xl md:text-5xl font-extrabold text-brand-secondary mb-6">
            Just Two People Who{' '}
            <span className="text-brand-accent">Love Helping Others Grow</span>
          </h3>
          <p className="text-gray-600 text-lg mb-6 leading-relaxed">
            We're a husband and wife team, and Revival Tech Firm is something we built together from the ground up. We started it because we kept seeing talented people struggle to put their value into words — and we knew we could help.
          </p>
          <p className="text-gray-600 text-lg mb-8 leading-relaxed">
            We don't try to do everything. We focus on what we're good at — helping you shape a resume and LinkedIn presence that feels authentic, professional, and genuinely you. That's it. And we do it with care.
          </p>
          <div className="space-y-5">
            {values.map((v, i) => (
              <motion.div
                key={i}
                className="flex items-start gap-4"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
              >
                <div className="w-11 h-11 rounded-full bg-brand-accent/10 flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={v.icon} className="text-brand-accent text-lg" />
                </div>
                <div>
                  <h4 className="font-bold text-brand-secondary mb-1">{v.title}</h4>
                  <p className="text-gray-500 text-sm">{v.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <div className="grid grid-cols-2 gap-6 mb-8">
            {stats.map((stat, i) => (
              <motion.div
                key={i}
                className="bg-white rounded-2xl p-6 shadow-md border border-gray-100 text-center"
                whileHover={{ y: -5 }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 * i }}
              >
                <div className="w-12 h-12 rounded-full bg-brand-accent/10 flex items-center justify-center mx-auto mb-3">
                  <SafeIcon icon={stat.icon} className="text-brand-accent text-xl" />
                </div>
                <p className="text-3xl font-extrabold text-brand-secondary">{stat.value}</p>
                <p className="text-gray-500 text-sm mt-1">{stat.label}</p>
              </motion.div>
            ))}
          </div>

          {/* Photo with scroll-triggered animation */}
          <motion.div
            className="relative rounded-2xl overflow-hidden shadow-xl"
            initial={{ opacity: 0, scale: 0.92, y: 40 }}
            whileInView={{ opacity: 1, scale: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.9, ease: 'easeOut', delay: 0.3 }}
          >
            {/* Shimmer sweep on entry */}
            <motion.div
              className="absolute inset-0 z-20 pointer-events-none"
              style={{
                background: 'linear-gradient(105deg, transparent 40%, rgba(59,223,248,0.25) 50%, transparent 60%)',
                backgroundSize: '200% 100%',
              }}
              initial={{ backgroundPosition: '200% 0' }}
              whileInView={{ backgroundPosition: '-200% 0' }}
              viewport={{ once: true }}
              transition={{ duration: 1.2, ease: 'easeInOut', delay: 0.5 }}
            />

            <motion.img
              src="https://cdn1.sharemyimage.com/smi/2026/04/22/RT_Photo-711x562-1.png"
              alt="Gary and Emily — Revival Tech Firm"
              className="w-full h-72 object-cover object-top"
              initial={{ scale: 1.1 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.1, ease: 'easeOut', delay: 0.3 }}
            />

            {/* Caption slides up */}
            <motion.div
              className="absolute inset-0 bg-gradient-to-t from-brand-secondary/70 to-transparent flex items-end p-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.8 }}
            >
              <div>
                <p className="text-white font-bold text-lg">Gary & Emily</p>
                <p className="text-gray-300 text-sm">We're in your corner — every step of the way.</p>
              </div>
            </motion.div>

            {/* Accent border glow */}
            <motion.div
              className="absolute inset-0 rounded-2xl border-2 border-brand-accent/0 pointer-events-none"
              initial={{ borderColor: 'rgba(59,223,248,0.6)' }}
              whileInView={{ borderColor: 'rgba(59,223,248,0)' }}
              viewport={{ once: true }}
              transition={{ duration: 1.5, delay: 0.5 }}
            />
          </motion.div>
        </motion.div>
      </div>
    </div>
  </section>
);

export default About;